import type * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';

import type { DiffSide } from './DiffSideContext';

export type DiffChangeType = 'added' | 'deleted' | 'modified';

/**
 * Shared diagonal-stripe background used for split-diff placeholders and spacers
 * that reserve the other side's height while indicating intentionally hidden content.
 */
export const DIAGONAL_STRIPE_CLASSNAME =
    'bg-[image:repeating-linear-gradient(-45deg,transparent,transparent_4.242px,var(--neutral-3)_4.242px,var(--neutral-3)_5.656px)]';

/** Left gutter halo outset in stacked view, including room for the gutter icon (px). */
export const HALO_H_ICON_OUTSET = 68;

/** Left gutter halo outset in split view, where no gutter icon is rendered (px). */
export const HALO_H_GUTTER_OUTSET = 24;

/**
 * Left outset applied to root-anchored halo and spacer geometry.
 */
export function computeHaloLeftOutset(diffSide: DiffSide | null): number {
    const isStacked = diffSide === null;
    return isStacked ? HALO_H_ICON_OUTSET : HALO_H_GUTTER_OUTSET;
}

export const DIFF_ICONS: Record<DiffChangeType, IconDefinition> = {
    added: Icon.DiffAdded,
    deleted: Icon.DiffRemoved,
    modified: Icon.DiffModified,
};

/** Order in which change types are shown in aggregate change-count surfaces. */
export const CHANGE_TYPES: DiffChangeType[] = ['added', 'modified', 'deleted'];

/** Delta symbol shown in front of each change count. */
export const CHANGE_TYPE_SYMBOLS: Record<DiffChangeType, string> = {
    added: '+',
    // Greek capital delta, the change between two states.
    modified: '\u0394',
    // Minus sign rather than a hyphen, so it is as wide as "+".
    deleted: '\u2212',
};

/**
 * Fills of the change counts bar. Added uses the Status success-dot green: the diff token green is
 * too dark for a thin bar.
 */
export const CHANGE_BAR_FILLS: Record<DiffChangeType, string> = {
    added: '#4dde98',
    modified: 'var(--sp-color-icon-diff-modified)',
    deleted: 'var(--sp-color-icon-diff-deleted)',
};

export const DIFF_TEXT_COLORS: Record<DiffChangeType, string> = {
    added: 'text-diff-added',
    deleted: 'text-diff-deleted',
    modified: 'text-diff-modified',
};

/**
 * Background treatments for diffed content, in two tiers:
 * - `area`: the softer band behind a whole changed region — e.g. the wide
 *   horizontal halo that `BlockDiffLayer` paints to mark an entire block.
 * - `highlight`: the equivalent of `area` but a shade darker, to briefly flash to draw attention
 * - `element`: the slightly darker bg on the actual changed text/node, so it
 *   reads with contrast against the `area` it sits within.
 */
export const DIFF_BACKGROUND_COLORS: Record<
    DiffChangeType,
    { area: string; highlight: string; element: string }
> = {
    added: {
        area: 'bg-diff-added',
        highlight: 'bg-[var(--green-3)]',
        element: 'bg-success-ui-active',
    },
    deleted: {
        area: 'bg-diff-deleted',
        highlight: 'bg-[var(--red-3)]',
        element: 'bg-danger-ui-active',
    },
    modified: {
        area: 'bg-diff-modified',
        highlight: 'bg-[var(--blue-3)]',
        element: 'bg-info-ui-active',
    },
};

/** Colour of the stripe on the left edge of a halo, a darker step than its `area` band. */
const DIFF_HALO_BORDER: Record<DiffChangeType, string> = {
    added: '#18a46c',
    deleted: 'var(--red-9)',
    modified: 'var(--blue-9)',
};

/**
 * The halo's left stripe, as an inset shadow so it takes no layout room. Shared by the block diff
 * layer and the library item cells, so a changed variable reads like a changed paragraph.
 */
export function getDiffHaloStripeStyle(type: DiffChangeType): React.CSSProperties {
    return { boxShadow: `inset 2px 0 0 0 ${DIFF_HALO_BORDER[type]}` };
}
